"use client"

import { useEffect, useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { Loader2, RotateCw, ShieldAlert } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { signOutThisDevice } from '@/lib/sign-out'
import { useStore } from '@/lib/store'
import { canOpen, homeFor } from '@/lib/roles'
import { useWaiterAccess } from '@/lib/use-waiter-access'
import { WaiterOff } from '@/components/waiter/waiter-off'
import { PayDialog } from '@/components/billing/pay-panel'
import { isPlayInstall } from '@/lib/channel'
import { isAuthNetworkError } from '@/lib/offline'
import { useT } from '@/lib/i18n'

export function StoreHydrator({ children }: { children: React.ReactNode }) {
  const t = useT()
  const router = useRouter()
  const isHydrated = useStore((s) => s.isHydrated)
  const loadFailed = useStore((s) => s.loadFailed)
  const currentOrg = useStore((s) => s.currentOrg)
  const isPlatformAdmin = useStore((s) => s.isPlatformAdmin)
  const userRole = useStore((s) => s.userRole)
  const waiter = useWaiterAccess()
  const supportSession = useStore((s) => s.supportSession)
  const isOnline = useStore((s) => s.isOnline)
  const pathname = usePathname()
  // 097 — a closed shop's owner pays from the lock screen itself
  const [payOpen, setPayOpen] = useState(false)

  /**
   * Hydrate — but FIRST check whose cache this is.
   *
   * The persisted store holds a whole shop and outlives a sign-out. The guard
   * used to be `if (!isHydrated) hydrate()`, and `isHydrated` is in-memory:
   * after signing out and back in as somebody else in the same tab it was
   * still true from the previous session, so hydrate never ran and the new
   * owner was shown THE PREVIOUS OWNER'S SHOP — org name, products, shifts,
   * settings. Two live owners hit it on 2026-09-09.
   *
   * RLS was never breached: the database would not have handed either of them
   * the other's rows. What they saw was a cached copy that nobody cleared.
   * That distinction is the reason this is a client bug and not a tenancy one
   * — and the reason a UI check is never the boundary.
   */
  useEffect(() => {
    void (async () => {
      const before = useStore.getState()
      let signedInAs: string | null = null
      // The session check is also the cheapest „is the server there?": auth
      // does not retry, while every boot read would sit out postgrest's
      // 1 + 2 + 4 s of retries before admitting the network is gone — the
      // wifi-up-internet-down case, where the browser still says online.
      let unreachable = false
      try {
        const { data, error } = await createClient().auth.getUser()
        signedInAs = data.user?.id ?? null
        unreachable = isAuthNetworkError(error)
      } catch (e) {
        signedInAs = null
        unreachable = isAuthNetworkError(e)
      }

      // ⚠️ Only clear when we POSITIVELY know it is somebody else. Offline,
      // `getUser()` answers null, and wiping on that would destroy the POS
      // survival kit exactly when it is the only thing keeping the till
      // running — the opposite of what the persisted store is for.
      if (signedInAs && before.cachedUserId && signedInAs !== before.cachedUserId) {
        before.clearTenantCache()
      }

      if (!useStore.getState().isHydrated) {
        useStore.getState().hydrate({ offline: unreachable })
      }
    })()
  }, [])

  // Offline while the browser says ONLINE (the wifi is up, the internet
  // behind it is not): no 'online' event will ever come to end it. Ask the
  // server again every 15 s; when it answers, read everything and send what
  // was sold meanwhile (2026-09-26).
  useEffect(() => {
    if (isOnline || !isHydrated) return
    const id = window.setInterval(async () => {
      if (!navigator.onLine) return   // the 'online' event handles this one
      try {
        const { error } = await createClient().auth.getUser()
        if (error) return
      } catch {
        return
      }
      window.clearInterval(id)
      const s = useStore.getState()
      s.setOnline(true)
      await s.hydrate()
      void s.syncOfflineSales()
    }, 15_000)
    return () => window.clearInterval(id)
  }, [isOnline, isHydrated])

  // Offline mode: track the connection and replay queued sales on reconnect.
  useEffect(() => {
    const s = useStore.getState()
    s.setOnline(navigator.onLine)
    if (navigator.onLine) s.syncOfflineSales() // drain anything left from a previous session

    const onOnline = () => {
      useStore.getState().setOnline(true)
      useStore.getState().syncOfflineSales()
    }
    const onOffline = () => useStore.getState().setOnline(false)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  // A platform admin with no shop of their own belongs in the god console,
  // not on an empty POS. Send them straight there.
  useEffect(() => {
    if (isHydrated && !currentOrg && isPlatformAdmin) {
      router.replace('/platform')
    }
  }, [isHydrated, currentOrg, isPlatformAdmin, router])

  // A cashier who types /accounting into the address bar is sent back to the
  // till. This is signposting, not security — RLS already returns nothing on
  // those screens (migration 057) — but a page that renders empty tables reads
  // as a broken app rather than as a boundary.
  //
  // Waits for hydrate: `userRole` is null until the membership is read, and
  // redirecting on null would bounce an OWNER off their own accounting for the
  // first second of every visit.
  useEffect(() => {
    if (!isHydrated || !currentOrg || !pathname) return
    if (!canOpen(pathname, userRole, waiter)) router.replace(homeFor(userRole, waiter))
  }, [isHydrated, currentOrg, userRole, waiter, pathname, router])

  if (!isHydrated) {
    return (
      <div className="flex h-dvh w-full flex-col items-center justify-center gap-4 bg-background">
        <div className="size-12 rounded-2xl bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center shadow-lg shadow-primary/25">
          <Loader2 className="size-6 text-white animate-spin" />
        </div>
        <p className="text-sm font-semibold text-muted-foreground">{t('hydrate.loading')}</p>
      </div>
    )
  }

  // A tenant is frozen (read-only, RLS blocks writes) when manually
  // suspended OR when a trial has been expired past its 3-day grace.
  // Platform admins are exempt so they can still operate.
  const DAY = 86_400_000
  const trialLocked =
    currentOrg?.plan === 'trial' && currentOrg.trial_ends_at
      ? Date.now() > new Date(currentOrg.trial_ends_at).getTime() + 3 * DAY
      : false
  const locked = currentOrg?.status === 'suspended' || trialLocked

  // 1.67 — a switched-off waiter is never a shared till: one screen, sign out (Codex #6)
  if (isHydrated && currentOrg && waiter === 'off') return <WaiterOff />

  if (locked && !isPlatformAdmin) {
    const signOut = async () => {
      useStore.getState().clearTenantCache()
      await signOutThisDevice(createClient())
      window.location.href = '/login'
    }
    return (
      <div className="flex h-dvh w-full flex-col items-center justify-center gap-5 bg-background p-6 text-center">
        <div className="size-16 rounded-3xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-xl shadow-red-500/25">
          <ShieldAlert className="size-8 text-white" />
        </div>
        <div className="max-w-md flex flex-col gap-2">
          <h1 className="text-xl font-black text-foreground">
            {trialLocked ? t('hydrate.trialEnded') : t('hydrate.suspended')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {userRole !== 'owner'
              ? t('hydrate.staffLocked')
              : currentOrg?.pay_claim_at
                ? t('hydrate.claimReceived')
                // The Google Play build says what happened, not how to pay (lib/channel.ts).
                : isPlayInstall()
                  ? (trialLocked
                      ? t('hydrate.playTrial')
                      : t('hydrate.playSuspended'))
                : trialLocked
                  ? t('hydrate.trialPay')
                  : t('hydrate.suspendedPay')}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2.5 w-full max-w-sm">
          {supportSession && (
            <button
              onClick={async () => {
                await useStore.getState().endSupportSession()
                useStore.getState().clearTenantCache()
                window.location.href = '/platform'
              }}
              className="flex-1 h-12 px-6 rounded-2xl bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-black transition-colors"
            >
              {t('hydrate.backToConsole')}
            </button>
          )}
          {userRole === 'owner' && !supportSession && !isPlayInstall() && (
            <button
              onClick={() => setPayOpen(true)}
              className="flex-1 h-12 px-6 rounded-2xl bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-black transition-colors"
            >
              {currentOrg?.pay_claim_at ? t('hydrate.payDetails') : t('hydrate.pay')}
            </button>
          )}
          <button
            onClick={signOut}
            className="flex-1 h-12 px-6 rounded-2xl border border-border bg-card dark:bg-white/5 hover:bg-accent text-sm font-bold text-foreground transition-colors"
          >
            {t('shell.signOut')}
          </button>
        </div>
        <PayDialog open={payOpen} onOpenChange={setPayOpen} />
      </div>
    )
  }

  // The boot read failed on the server — the shop exists, its rows did not
  // come (2026-09-30: a timeout here read as „no shop on this account").
  // Say so and offer the one thing that helps.
  if (!currentOrg && loadFailed && !isPlatformAdmin) {
    return (
      <div className="flex h-dvh w-full flex-col items-center justify-center gap-5 bg-background p-6 text-center">
        <div className="size-16 rounded-3xl bg-primary/10 text-primary flex items-center justify-center">
          <RotateCw className="size-8" />
        </div>
        <div className="max-w-md flex flex-col gap-2">
          <h1 className="text-xl font-black text-foreground">{t('hydrate.failedTitle')}</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">{t('hydrate.failedBody')}</p>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="h-12 px-6 rounded-2xl bg-primary text-primary-foreground text-sm font-bold transition-transform active:scale-[0.97]"
        >
          {t('hydrate.retry')}
        </button>
      </div>
    )
  }

  // No organization on the account.
  if (!currentOrg) {
    // Platform admin → redirecting to /platform (handled by the effect above).
    if (isPlatformAdmin) {
      return (
        <div className="flex h-dvh w-full flex-col items-center justify-center gap-4 bg-background">
          <div className="size-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
            <Loader2 className="size-6 text-white animate-spin" />
          </div>
          <p className="text-sm font-semibold text-muted-foreground">{t('hydrate.toPlatform')}</p>
        </div>
      )
    }
    // Orphan user (no org, not an admin) — shouldn't normally happen.
    const signOut = async () => {
      useStore.getState().clearTenantCache()
      await signOutThisDevice(createClient())
      window.location.href = '/login'
    }
    return (
      <div className="flex h-dvh w-full flex-col items-center justify-center gap-5 bg-background p-6 text-center">
        <div className="size-16 rounded-3xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-xl shadow-amber-500/25">
          <ShieldAlert className="size-8 text-white" />
        </div>
        <div className="max-w-md flex flex-col gap-2">
          <h1 className="text-xl font-black text-foreground">{t('hydrate.noShop')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('hydrate.noShopBody')}
          </p>
        </div>
        <button
          onClick={signOut}
          className="h-11 px-6 rounded-2xl border border-border bg-card dark:bg-white/5 hover:bg-accent text-sm font-bold text-foreground transition-colors"
        >
          {t('shell.signOut')}
        </button>
      </div>
    )
  }

  return <>{children}</>
}
